import debounce from '../helpers/debounce';
import { DirectiveOptions } from 'vue';

const elements: { [name: string]: HTMLElement[] } = {};

const heightSetters: { [name: string]: () => void } = {};
const autoHeightSetters: { [name: string]: () => void } = {};

const matchHeight: DirectiveOptions = {
	inserted(el, binding) {
		const name = <string>binding.value;
		const enabled = binding.arg ?? true;

		if (!elements[name]) {
			elements[name] = [];
		}

		if (elements[name].indexOf(el) === -1) {
			elements[name].push(el);
		}

		if (!heightSetters[name]) {
			heightSetters[name] = createHeightSetter(name);

			if (enabled) {
				window.addEventListener('resize', heightSetters[name], { passive: true });
			}
		}

		if (!autoHeightSetters[name]) {
			autoHeightSetters[name] = createAutoHeightSetter(name);
		}

		if (enabled) {
			heightSetters[name]();
		}
	},
	componentUpdated(el, binding) {
		const name = <string>binding.value;
		window.removeEventListener('resize', heightSetters[name]);
		window.removeEventListener('resize', autoHeightSetters[name]);

		const enabled = binding.arg ?? true;
		if (enabled) {
			window.addEventListener('resize', heightSetters[name]);
		} else {
			window.addEventListener('resize', autoHeightSetters[name]);
		}
	},
	unbind(el, binding) {
		const name = <string>binding.value;

		const index = elements[name].indexOf(el);
		if (index > -1) {
			elements[name].splice(index, 1);
		}

		if (elements[name].length === 0) {
			window.removeEventListener('resize', heightSetters[name]);
			heightSetters[name] = null;

			window.removeEventListener('resize', autoHeightSetters[name]);
			autoHeightSetters[name] = null;

			return;
		}

		const enabled = binding.arg ?? true;
		if (enabled) {
			heightSetters[name]();
		}
	}
};

export default matchHeight;

function createHeightSetter(name: string): (...args: any) => void {
	return debounce(60, function setHeights() {
		setElementsToHeight(elements[name], null);

		let tallest = 0;

		for (const el of elements[name]) {
			const { offsetHeight } = el;

			if (offsetHeight > tallest) {
				tallest = offsetHeight;
			}
		}

		setElementsToHeight(elements[name], tallest + 'px');
	});
}

function createAutoHeightSetter(name: string): (...args: any) => void {
	return debounce(60, () => setElementsToHeight(elements[name], null));
}

function setElementsToHeight(elements: HTMLElement[], height: string): void {
	for (const el of elements) {
		el.style.height = height;
	}
}